import { Alert, Button, CircularProgress, Grid, Stack } from '@mui/material'
import { FormLangTab } from 'Components/Tabs'
import { InputUI, TextEditorUI } from 'Components/UI'
import { Controller, useFormState, useWatch } from 'react-hook-form'
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { APP } from 'Constants/App'
import { formLangTab } from 'Data/app'
import { IKoreanSchoolContent } from 'Entities/koreanSchoolContent'
import { LangError } from 'Types/app'
import {
  areKoreanSchoolContentValuesEqual,
  useKoreanLanguageConfig,
  useKoreanSchoolContentForm,
  type KoreanSchoolContentFormValues,
} from 'Views/KoreanLanguage/Hooks'

const ALL_LANGUAGES_REQUIRED_MESSAGE =
  'Заполните заголовок и текст на всех трёх языках: русский, узбекский и корейский. Переключите вкладки и проверьте поля с ошибками.'

const NO_CHANGES_MESSAGE =
  'Изменений нет: текущие значения совпадают с сохранёнными. Запрос на сервер не отправлен.'

type Props = {
  data?: IKoreanSchoolContent
}

export const KoreanSchoolContentForm = ({ data }: Props) => {
  const { schoolContentBodyType } = useKoreanLanguageConfig()
  const [multiLangWarning, setMultiLangWarning] = useState('')
  const [noChangesNotice, setNoChangesNotice] = useState('')

  const seed = useMemo((): KoreanSchoolContentFormValues | null => {
    if (!data) {
      return null
    }
    return {
      titleRu: data.titleRu ?? '',
      titleUz: data.titleUz ?? '',
      titleKr: data.titleKr ?? '',
      contentRu: data.contentRu ?? '',
      contentUz: data.contentUz ?? '',
      contentKr: data.contentKr ?? '',
    }
  }, [data])

  const { form, onSubmit, isLoading } = useKoreanSchoolContentForm(
    schoolContentBodyType,
    data?.id?.toString(),
    seed,
  )
  const { control, setValue, handleSubmit, setError, reset, trigger, formState: { errors } } = form
  const { isValid } = useFormState({ control })
  const watchedValues = useWatch({ control }) as Partial<KoreanSchoolContentFormValues> | undefined

  useEffect(() => {
    if (isValid) {
      setMultiLangWarning('')
    }
  }, [isValid])

  useEffect(() => {
    if (!seed || !data?.id) {
      return
    }
    const cur: KoreanSchoolContentFormValues = {
      titleRu: '',
      titleUz: '',
      titleKr: '',
      contentRu: '',
      contentUz: '',
      contentKr: '',
      ...(watchedValues ?? {}),
    }
    if (!areKoreanSchoolContentValuesEqual(cur, seed)) {
      setNoChangesNotice('')
    }
  }, [data?.id, seed, watchedValues])

  useEffect(() => {
    setNoChangesNotice('')
    if (data) {
      reset({
        titleRu: data.titleRu ?? '',
        titleUz: data.titleUz ?? '',
        titleKr: data.titleKr ?? '',
        contentRu: data.contentRu ?? '',
        contentUz: data.contentUz ?? '',
        contentKr: data.contentKr ?? '',
      })
      void trigger()
    }
  }, [data, reset, trigger])

  const langError = useMemo<Partial<LangError>>(
    () => ({
      Ru: !!(errors.titleRu?.message || errors.contentRu?.message),
      Uz: !!(errors.titleUz?.message || errors.contentUz?.message),
      Kr: !!(errors.titleKr?.message || errors.contentKr?.message),
    }),
    [errors],
  )

  const onInvalid = useCallback(() => {
    setMultiLangWarning(ALL_LANGUAGES_REQUIRED_MESSAGE)
  }, [])

  const submitValid = useCallback(
    (values: KoreanSchoolContentFormValues) => {
      setMultiLangWarning('')
      if (data?.id && seed && areKoreanSchoolContentValuesEqual(values, seed)) {
        setNoChangesNotice(NO_CHANGES_MESSAGE)
        return
      }
      setNoChangesNotice('')
      onSubmit(values)
    },
    [data?.id, onSubmit, seed],
  )

  return (
    <FormLangTab
      error={langError}
      render={lang => (
        <form onSubmit={handleSubmit(submitValid, onInvalid)} noValidate>
          <Grid container rowSpacing={3}>
            {multiLangWarning ? (
              <Grid item xs={12}>
                <Alert severity='warning' onClose={() => setMultiLangWarning('')}>
                  {multiLangWarning}
                </Alert>
              </Grid>
            ) : null}
            {noChangesNotice ? (
              <Grid item xs={12}>
                <Alert severity='info' onClose={() => setNoChangesNotice('')}>
                  {noChangesNotice}
                </Alert>
              </Grid>
            ) : null}
            {formLangTab.map(({ id }) => {
              const titleName: any = `title${id}`
              const contentName: any = `content${id}`
              return (
                <Fragment key={id}>
                  {lang === id && (
                    <>
                      <Grid item xs={12}>
                        <Controller
                          name={titleName}
                          control={control}
                          render={({ field, fieldState: { error } }) => (
                            <InputUI
                              {...field}
                              label={`${APP.TITLE} (${id})`}
                              placeholder={APP.ENTER_TITLE}
                              error={!!error?.message}
                              helperText={error?.message}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Controller
                          name={contentName}
                          control={control}
                          render={({ field: { value }, fieldState: { error } }) => (
                            <TextEditorUI
                              label={`${APP.TEXT} (${id})`}
                              value={value}
                              errorText={error?.message}
                              onChange={next => {
                                if (next.length > 0) {
                                  setError(contentName, {})
                                }
                                setValue(contentName, next)
                              }}
                            />
                          )}
                        />
                      </Grid>
                    </>
                  )}
                </Fragment>
              )
            })}
            <Grid item xs={12} marginTop={1}>
              <Stack direction='row' spacing={2}>
                <Button
                  size='large'
                  type='submit'
                  startIcon={isLoading ? <CircularProgress size={20} color='inherit' /> : undefined}
                  disabled={isLoading}>
                  Сохранить
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </form>
      )}
    />
  )
}
